import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, Building2, Wrench, Radar, PlusCircle, PencilLine, Settings, Menu, LogOut, ExternalLink, LogIn, UserPlus, Inbox, ShieldCheck, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Logo } from '@/components/brand/Logo';
import { CompanyAvatar } from '@/components/common/Primitives';
import { useData } from '@/hooks/useData';
import { authService } from '@/services/api';
import { cn } from '@/lib/utils';

export const DASH_NAV = [
  { to: '/painel', label: 'Visão geral', icon: LayoutDashboard, end: true },
  { to: '/painel/perfil', label: 'Meu perfil empresarial', icon: Building2 },
  { to: '/painel/servicos', label: 'Meus serviços', icon: Wrench },
  { to: '/painel/oportunidades', label: 'Minhas oportunidades', icon: Radar },
  { to: '/painel/publicar', label: 'Publicar oportunidade', icon: PlusCircle },
  { to: '/painel/publicacoes', label: 'Editar publicações', icon: PencilLine },
  { to: '/painel/anunciar', label: 'Anunciar na capa', icon: Megaphone },
  { to: '/painel/mensagens', label: 'Caixa de entrada', icon: Inbox },
  { to: '/painel/configuracoes', label: 'Configurações da conta', icon: Settings },
];

// Item extra, visível apenas para administradores
const ADMIN_ITEM = { to: '/admin', label: 'Administração', icon: ShieldCheck };

export const SideNav = ({ items, testId }) => (
  <nav className="flex flex-col gap-1" data-testid={testId}>
    {items.map(({ to, label, icon: Icon, end }) => (
      <NavLink key={to} to={to} end={end} className={({ isActive }) => cn('group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors', isActive ? 'border border-white/10 bg-white/5 text-foreground shadow-[0_0_0_1px_rgba(34,211,238,0.14)] before:absolute before:inset-y-2 before:left-0 before:w-[2px] before:rounded before:bg-primary' : 'border border-transparent text-muted-foreground hover:bg-white/5 hover:text-foreground')} data-testid={`sidenav-${to.split('/').filter(Boolean).join('-')}`}>
        <Icon className="h-4 w-4" /> {label}
      </NavLink>
    ))}
  </nav>
);

const Gate = () => (
  <div className="flex min-h-screen items-center justify-center bg-[var(--void)] px-4">
    <div className="surface w-full max-w-md p-8 text-center" data-testid="dashboard-gate">
      <div className="flex justify-center"><Logo /></div>
      <h1 className="mt-6 text-xl font-semibold">Painel do empresário</h1>
      <p className="mt-2 text-sm text-muted-foreground">Entre ou crie sua conta para gerenciar o perfil da sua empresa e publicar oportunidades.</p>
      <div className="mt-6 grid gap-2">
        <Button asChild className="btn-glow" data-testid="gate-login-button"><Link to="/entrar" state={{ from: '/painel' }}><LogIn /> Entrar</Link></Button>
        <Button asChild variant="secondary" className="border border-white/10"><Link to="/criar-conta"><UserPlus /> Criar conta</Link></Button>
      </div>
      <Link to="/" className="mt-6 inline-block text-xs text-muted-foreground hover:text-foreground">Voltar ao site</Link>
    </div>
  </div>
);

export default function DashboardLayout() {
  const { user, myCompany, authLoading } = useData();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  if (authLoading) return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Carregando…</div>;
  if (!user) return <Gate />;

  const logout = async () => {
    await authService.logout();
    toast.success('Sessão encerrada');
  };
  const needsCompany = !myCompany && !location.pathname.startsWith('/painel/configuracoes');
  const navItems = user.role === 'admin' ? [...DASH_NAV, ADMIN_ITEM] : DASH_NAV;

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <Link to="/" className="px-2"><Logo /></Link>
      {myCompany && (
        <div className="mt-6 flex items-center gap-3 rounded-lg border border-white/10 bg-[#0b0f16] p-3">
          <CompanyAvatar company={myCompany} size={36} />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{myCompany.name}</div>
            <div className="truncate text-[11px] text-muted-foreground">{user.name}</div>
          </div>
        </div>
      )}
      <div className="mt-6"><SideNav items={navItems} testId="dashboard-sidebar-nav" /></div>
      <div className="mt-auto space-y-2 pt-6">
        {myCompany && <Button asChild variant="ghost" size="sm" className="w-full justify-start text-muted-foreground"><Link to={`/empresas/${myCompany.id}`}><ExternalLink /> Ver perfil público</Link></Button>}
        <Button variant="ghost" size="sm" className="w-full justify-start text-muted-foreground" onClick={logout} data-testid="dashboard-logout-button"><LogOut /> Sair</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--void)]">
      <aside className="hidden border-r border-white/10 bg-[#07090d] p-4 lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">{SidebarInner}</aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-white/10 bg-[rgba(7,8,11,0.85)] px-4 backdrop-blur-md sm:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Abrir menu do painel" data-testid="dashboard-sidebar-open-button"><Menu /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] border-white/10 bg-[#07090d] p-4" onClick={(e) => e.target.closest('a') && setOpen(false)}>
              <SheetHeader className="sr-only"><SheetTitle>Menu do painel</SheetTitle></SheetHeader>
              {SidebarInner}
            </SheetContent>
          </Sheet>
          <div className="font-display text-sm font-semibold">Painel do empresário</div>
        </header>
        <main className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          {needsCompany ? (
            <div className="surface mx-auto max-w-lg p-8 text-center" data-testid="dashboard-needs-company">
              <Building2 className="mx-auto h-8 w-8 text-primary" />
              <h1 className="mt-3 text-xl font-semibold">Cadastre sua empresa</h1>
              <p className="mt-2 text-sm text-muted-foreground">Sua conta ainda não tem uma empresa vinculada. Complete o cadastro para usar o painel.</p>
              <Button asChild className="btn-glow mt-5" data-testid="dashboard-register-company-button"><Link to="/cadastrar-empresa">Cadastrar empresa</Link></Button>
            </div>
          ) : <Outlet />}
        </main>
      </div>
    </div>
  );
}